/**
 * Date / date-time formatters used across the PQR module so report
 * history, picker hints, and inline timestamps all read the same way:
 *
 *   `04 Jun 2026`            (formatPqrDate)
 *   `04 Jun 2026 13:45 UTC`  (formatPqrDateTime)
 *
 * All output is rendered in UTC regardless of the viewer's locale so a
 * report generated by an operator in Houston reads identically to the
 * same report opened by a reviewer in Bangalore. Callers should
 * surface a small "Times shown in UTC" caption near any cluster of
 * formatted values so the convention is obvious.
 *
 * Added in pqr-report-history-and-rev polish-pass slice 06; supersedes
 * the bespoke `toLocaleString()` / `toLocaleDateString()` calls that
 * previously rendered locale-dependent and timezone-dependent output.
 */

const MONTHS_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
] as const

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n)
}

function parseUtc(iso?: string): Date | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return d
}

/** `04 Jun 2026` — UTC calendar date. Empty string on bad input. */
export function formatPqrDate(iso?: string): string {
  const d = parseUtc(iso)
  if (!d) return ''
  return `${pad2(d.getUTCDate())} ${MONTHS_SHORT[d.getUTCMonth()]} ${d.getUTCFullYear()}`
}

/** `11 June 2026` — UTC calendar date with full month name (What's New, etc.). */
export function formatWhatsNewDate(iso?: string): string {
  const d = parseUtc(iso?.includes('T') ? iso : iso ? `${iso}T00:00:00.000Z` : undefined)
  if (!d) return iso?.trim() || ''
  return d.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC'
  })
}

/** `04 Jun 2026 13:45 UTC` — 24-hour clock. Empty string on bad input. */
export function formatPqrDateTime(iso?: string): string {
  const d = parseUtc(iso)
  if (!d) return ''
  return `${formatPqrDate(iso)} ${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())} UTC`
}
